import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import axios from 'axios';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Input } from '../ui/input';
import { Button } from '../ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { ArrowLeft, Plus, Trash2, Copy, Users, FileText, Sparkles } from 'lucide-react';
import { toast } from 'sonner';
import { ProjectForm } from './ProjectForm';
import { PromptGenerator } from './PromptGenerator';
import { HomeSectionForm } from '../admin/HomeSectionForm';
import { SigContentForm } from '../admin/SigContentForm';
import { OnIaGolfContentForm } from '../admin/OnIaGolfContentForm';
import { CommunityHelpForm } from '../admin/CommunityHelpForm';
import { NutsMembersAdmin } from '../admin/NutsMembersAdmin';
import { ForumRoomsAdmin } from '../admin/ForumRoomsAdmin';
import { PendingAvatarsAdmin } from '../admin/PendingAvatarsAdmin';
import { PolaroidsAdmin } from '../admin/PolaroidsAdmin';
import { GolfToursAdmin } from '../admin/GolfToursAdmin';
import { AmazonStoreAdmin } from '../admin/AmazonStoreAdmin';
import { HomeVideoAdmin } from '../admin/HomeVideoAdmin';
import { HomeMusicAdmin } from '../admin/HomeMusicAdmin';
import { GalleryAdmin } from '../admin/GalleryAdmin';

const API_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

export const AdminPanel = () => {
  const [activeTab, setActiveTab] = useState('code');
  const [currentCode, setCurrentCode] = useState(null);
  const [masterCode, setMasterCode] = useState(null);
  const [projects, setProjects] = useState([]);
  const [collaborators, setCollaborators] = useState([]);
  const [questionnaires, setQuestionnaires] = useState([]);
  const [homeSections, setHomeSections] = useState([]);
  const [loading, setLoading] = useState(false);
  
  // Prompt Generator state
  const [promptDialogOpen, setPromptDialogOpen] = useState(false);
  const [selectedProject, setSelectedProject] = useState(null);
  const [loadingProject, setLoadingProject] = useState(false);
  
  // Home Sections state
  const [editingSection, setEditingSection] = useState(null);
  const [showSectionForm, setShowSectionForm] = useState(false);
  
  // Collaborator form
  const [collaboratorForm, setCollaboratorForm] = useState({
    email: '',
    password: ''
  });
  
  const { user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (user?.role !== 'admin') {
      navigate('/zona-pro/dashboard');
      return;
    }
    
    loadData();
  }, [activeTab]);

  const loadData = async () => {
    setLoading(true);
    try {
      if (activeTab === 'code') {
        const weeklyResponse = await axios.get(`${API_URL}/api/zona-pro/current-code`);
        setCurrentCode(weeklyResponse.data);
        
        const masterResponse = await axios.get(`${API_URL}/api/zona-pro/master-code`);
        setMasterCode(masterResponse.data);
      } else if (activeTab === 'projects') {
        const response = await axios.get(`${API_URL}/api/zona-pro/projects`);
        setProjects(response.data);
      } else if (activeTab === 'collaborators') {
        const response = await axios.get(`${API_URL}/api/zona-pro/collaborators`);
        setCollaborators(response.data);
      } else if (activeTab === 'questionnaires') {
        const response = await axios.get(`${API_URL}/api/zona-pro/questionnaires`);
        setQuestionnaires(response.data);
      } else if (activeTab === 'home-sections') {
        const response = await axios.get(`${API_URL}/api/home-sections/all`);
        setHomeSections(response.data);
      }
    } catch (error) {
      toast.error('Error cargando datos');
    } finally {
      setLoading(false);
    }
  };

  const copyCode = () => {
    if (currentCode) {
      // Método alternativo compatible con más navegadores
      const textArea = document.createElement('textarea');
      textArea.value = currentCode.code;
      textArea.style.position = 'fixed';
      textArea.style.left = '-999999px';
      document.body.appendChild(textArea);
      textArea.select();
      try {
        document.execCommand('copy');
        toast.success('Código copiado al portapapeles');
      } catch (err) {
        toast.error('No se pudo copiar el código');
      }
      document.body.removeChild(textArea);
    }
  };

  const handleCreateProject = async (projectData) => {
    setLoading(true);
    
    try {
      await axios.post(`${API_URL}/api/zona-pro/projects`, {
        ...projectData,
        order: projects.length
      });
      
      toast.success('Proyecto creado correctamente');
      loadData();
    } catch (error) {
      toast.error('Error creando proyecto');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteProject = async (projectId) => {
    if (!confirm('¿Seguro que quieres eliminar este proyecto?')) return;
    
    try {
      await axios.delete(`${API_URL}/api/zona-pro/projects/${projectId}`);
      toast.success('Proyecto eliminado');
      loadData();
    } catch (error) {
      toast.error('Error eliminando proyecto');
    }
  };

  const handleCreateCollaborator = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      await axios.post(`${API_URL}/api/zona-pro/collaborators`, collaboratorForm);
      toast.success('Colaborador creado correctamente');
      setCollaboratorForm({ email: '', password: '' });
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error creando colaborador');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteCollaborator = async (userId) => {
    if (!confirm('¿Seguro que quieres eliminar este colaborador?')) return;
    
    try {
      await axios.delete(`${API_URL}/api/zona-pro/collaborators/${userId}`);
      toast.success('Colaborador eliminado');
      loadData();
    } catch (error) {
      toast.error('Error eliminando colaborador');
    }
  };

  const handleDeleteQuestionnaire = async (questionnaireId) => {
    if (!confirm('¿Seguro que quieres eliminar este cuestionario?')) return;
    
    try {
      await axios.delete(`${API_URL}/api/zona-pro/questionnaires/${questionnaireId}`);
      toast.success('Cuestionario eliminado');
      loadData();
    } catch (error) {
      toast.error('Error eliminando cuestionario');
    }
  };

  const handleGeneratePrompts = async (projectId) => {
    setLoadingProject(true);
    try {
      // Fetch full project details including actions
      const response = await axios.get(`${API_URL}/api/zona-pro/projects/${projectId}`);
      setSelectedProject(response.data);
      setPromptDialogOpen(true);
    } catch (error) {
      toast.error('Error cargando proyecto');
      console.error('Error loading project:', error);
    } finally {
      setLoadingProject(false);
    }
  };

  // Home Sections handlers
  const handleCreateSection = async (sectionData) => {
    setLoading(true);
    try {
      await axios.post(`${API_URL}/api/home-sections`, sectionData);
      toast.success('Sección creada');
      setShowSectionForm(false);
      loadData();
    } catch (error) {
      toast.error('Error creando sección');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateSection = async (sectionData) => {
    setLoading(true);
    try {
      await axios.put(`${API_URL}/api/home-sections/${editingSection.id}`, sectionData);
      toast.success('Sección actualizada');
      setEditingSection(null);
      setShowSectionForm(false);
      loadData();
    } catch (error) {
      toast.error('Error actualizando sección');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteSection = async (sectionId) => {
    if (!confirm('¿Seguro que quieres eliminar esta sección?')) return;
    
    try {
      await axios.delete(`${API_URL}/api/home-sections/${sectionId}`);
      toast.success('Sección eliminada');
      loadData();
    } catch (error) {
      toast.error('Error eliminando sección');
    }
  };

  const handleReorderSections = async (newOrder) => {
    try {
      const sectionIds = newOrder.map(section => section.id);
      await axios.put(`${API_URL}/api/home-sections/reorder`, sectionIds);
      toast.success('Orden actualizado');
      loadData();
    } catch (error) {
      toast.error('Error reordenando secciones');
    }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-white shadow-sm">
        <div className="container mx-auto px-6 py-4 flex justify-between items-center">
          <h1 className="text-2xl font-serif font-bold text-slate-800">Panel Admin</h1>
          <Button
            onClick={() => navigate('/zona-pro/dashboard')}
            variant="outline"
            size="sm"
          >
            <ArrowLeft className="mr-2 h-4 w-4" />
            Volver
          </Button>
        </div>
      </header>

      {/* Layout: sidebar (tabs) + content */}
      <div className="container mx-auto px-4 md:px-6 py-6">
        <div className="flex flex-col md:flex-row gap-6">
          {/* Sidebar tabs */}
          <aside className="md:w-60 shrink-0 md:sticky md:top-4 md:self-start">
            <nav className="flex md:flex-col gap-2 overflow-x-auto md:overflow-visible pb-1 md:pb-0">
          <Button
            variant={activeTab === 'code' ? 'default' : 'outline'}
            onClick={() => setActiveTab('code')}
            size="sm"
            className="md:justify-start md:w-full"
          >
            Código Diario
          </Button>
          <Button
            variant={activeTab === 'projects' ? 'default' : 'outline'}
            onClick={() => setActiveTab('projects')}
            size="sm"
            className="md:justify-start md:w-full"
          >
            <FileText className="mr-2 h-4 w-4" />
            Proyectos
          </Button>
          <Button
            variant={activeTab === 'collaborators' ? 'default' : 'outline'}
            onClick={() => setActiveTab('collaborators')}
            size="sm"
            className="md:justify-start md:w-full"
          >
            <Users className="mr-2 h-4 w-4" />
            Colaboradores
          </Button>
          <Button
            variant={activeTab === 'questionnaires' ? 'default' : 'outline'}
            onClick={() => setActiveTab('questionnaires')}
            size="sm"
            className="md:justify-start md:w-full"
          >
            Cuestionarios
          </Button>
          <Button
            variant={activeTab === 'home-sections' ? 'default' : 'outline'}
            onClick={() => setActiveTab('home-sections')}
            size="sm"
            className="bg-gradient-to-r from-purple-600 to-blue-600 md:justify-start md:w-full"
          >
            🏠 Secciones Home
          </Button>
          <Button
            variant={activeTab === 'sig-content' ? 'default' : 'outline'}
            onClick={() => setActiveTab('sig-content')}
            size="sm"
            className="bg-gradient-to-r from-emerald-700 to-yellow-600 text-white md:justify-start md:w-full"
            data-testid="tab-sig-content"
          >
            ⛳ Funnel SIG
          </Button>
          <Button
            variant={activeTab === 'on-ia-golf' ? 'default' : 'outline'}
            onClick={() => setActiveTab('on-ia-golf')}
            size="sm"
            className="bg-gradient-to-r from-slate-700 to-emerald-700 text-white md:justify-start md:w-full"
            data-testid="tab-on-ia-golf"
          >
            🧠 ON-IA-GOLF
          </Button>
          <Button
            variant={activeTab === 'community-help' ? 'default' : 'outline'}
            onClick={() => setActiveTab('community-help')}
            size="sm"
            className="bg-gradient-to-r from-amber-600 to-emerald-700 text-white md:justify-start md:w-full"
            data-testid="tab-community-help"
          >
            ❓ Ajuda comunitats
          </Button>
          <Button
            variant={activeTab === 'nuts-members' ? 'default' : 'outline'}
            onClick={() => setActiveTab('nuts-members')}
            size="sm"
            className="bg-gradient-to-r from-amber-700 to-red-600 text-white md:justify-start md:w-full"
            data-testid="tab-nuts-members"
          >
            🥜 Membres Nuts
          </Button>
          <Button
            variant={activeTab === 'forum-rooms' ? 'default' : 'outline'}
            onClick={() => setActiveTab('forum-rooms')}
            size="sm"
            className="bg-gradient-to-r from-emerald-600 to-amber-600 text-white md:justify-start md:w-full"
            data-testid="tab-forum-rooms"
          >
            💬 Sales del Fòrum
          </Button>
          <Button
            variant={activeTab === 'pending-avatars' ? 'default' : 'outline'}
            onClick={() => setActiveTab('pending-avatars')}
            size="sm"
            className="bg-gradient-to-r from-fuchsia-600 to-amber-500 text-white md:justify-start md:w-full"
            data-testid="tab-pending-avatars"
          >
            🖼️ Avatars pendents
          </Button>
          <Button
            variant={activeTab === 'polaroids' ? 'default' : 'outline'}
            onClick={() => setActiveTab('polaroids')}
            size="sm"
            className="bg-gradient-to-r from-pink-600 to-amber-600 text-white md:justify-start md:w-full"
            data-testid="tab-polaroids"
          >
            🎁 Polaroids
          </Button>
          <Button
            variant={activeTab === 'golf-tours' ? 'default' : 'outline'}
            onClick={() => setActiveTab('golf-tours')}
            size="sm"
            className="bg-gradient-to-r from-emerald-700 to-amber-500 text-white md:justify-start md:w-full"
            data-testid="tab-golf-tours"
          >
            🏌️ Golf Tours
          </Button>
          <Button
            variant={activeTab === 'amazon-store' ? 'default' : 'outline'}
            onClick={() => setActiveTab('amazon-store')}
            size="sm"
            className="bg-gradient-to-r from-amber-600 to-orange-500 text-white md:justify-start md:w-full"
            data-testid="tab-amazon-store"
          >
            🛒 Botiga Amazon
          </Button>
          <Button
            variant={activeTab === 'home-video' ? 'default' : 'outline'}
            onClick={() => setActiveTab('home-video')}
            size="sm"
            className="bg-gradient-to-r from-red-600 to-pink-600 text-white md:justify-start md:w-full"
            data-testid="tab-home-video"
          >
            🎥 Vídeo Home
          </Button>
          <Button
            variant={activeTab === 'home-music' ? 'default' : 'outline'}
            onClick={() => setActiveTab('home-music')}
            size="sm"
            className="bg-gradient-to-r from-indigo-600 to-purple-500 text-white md:justify-start md:w-full"
            data-testid="tab-home-music"
          >
            🎵 Música
          </Button>
          <Button
            variant={activeTab === 'gallery' ? 'default' : 'outline'}
            onClick={() => setActiveTab('gallery')}
            size="sm"
            className="bg-gradient-to-r from-purple-600 to-fuchsia-500 text-white md:justify-start md:w-full"
            data-testid="tab-gallery"
          >
            📷 Galeria fotos
          </Button>
            </nav>
          </aside>

          {/* Content */}
          <main className="flex-1 min-w-0" data-testid="admin-content">

        {/* Code Tabs */}
        {activeTab === 'code' && (
          <div className="space-y-6">
            {/* Weekly Code */}
            {currentCode && (
              <Card>
                <CardHeader>
                  <CardTitle>Código Semanal</CardTitle>
                  <CardDescription>
                    Comparte este código con visitantes temporales (caduca cada 7 días)
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    <div className="flex items-center gap-4">
                      <div className="flex-1 bg-slate-100 p-6 rounded-lg">
                        <p className="text-4xl font-bold text-center tracking-widest text-emerald-600">
                          {currentCode.code}
                        </p>
                      </div>
                      <Button onClick={copyCode} size="lg">
                        <Copy className="mr-2 h-4 w-4" />
                        Copiar
                      </Button>
                    </div>
                    <div className="text-sm text-slate-600">
                      <p>Válido desde: {new Date(currentCode.valid_from).toLocaleDateString('es-ES', {
                        weekday: 'long',
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric'
                      })}</p>
                      <p>Válido hasta: {new Date(currentCode.valid_until).toLocaleDateString('es-ES', {
                        weekday: 'long',
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric'
                      })}</p>
                      <p className="mt-2 text-emerald-600 font-medium">
                        ⏱️ Validez: 7 días (una semana)
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Master Code */}
            {masterCode && (
              <Card className="border-2 border-amber-400">
                <CardHeader className="bg-gradient-to-r from-amber-50 to-yellow-50">
                  <CardTitle className="flex items-center gap-2">
                    <span>🔑</span> Código Maestro (Llave Universal)
                  </CardTitle>
                  <CardDescription>
                    Este código NUNCA caduca. Úsalo para evaluadores, profesores o acceso permanente.
                  </CardDescription>
                </CardHeader>
                <CardContent className="pt-6">
                  <div className="space-y-4">
                    <div className="flex items-center gap-4">
                      <div className="flex-1 bg-gradient-to-r from-amber-100 to-yellow-100 p-6 rounded-lg border-2 border-amber-300">
                        <p className="text-3xl font-bold text-center tracking-widest text-amber-700">
                          {masterCode.code}
                        </p>
                      </div>
                      <Button 
                        onClick={() => {
                          const textArea = document.createElement('textarea');
                          textArea.value = masterCode.code;
                          textArea.style.position = 'fixed';
                          textArea.style.left = '-999999px';
                          document.body.appendChild(textArea);
                          textArea.select();
                          document.execCommand('copy');
                          document.body.removeChild(textArea);
                          toast.success('Código maestro copiado');
                        }} 
                        size="lg"
                        className="bg-amber-600 hover:bg-amber-700"
                      >
                        <Copy className="mr-2 h-4 w-4" />
                        Copiar
                      </Button>
                    </div>
                    <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                      <p className="text-sm text-amber-800 font-medium">
                        ✨ Este código no caduca nunca
                      </p>
                      <p className="text-xs text-amber-700 mt-1">
                        Ideal para: profesores evaluadores, colaboradores externos, acceso de larga duración
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        )}

        {/* Projects Tab */}
        {activeTab === 'projects' && (
          <div className="space-y-6">
            {/* Create Project Form */}
            <Card>
              <CardHeader>
                <CardTitle>Crear Nuevo Proyecto</CardTitle>
                <CardDescription>
                  Los proyectos pueden tener múltiples acciones/fases
                </CardDescription>
              </CardHeader>
              <CardContent>
                <ProjectForm
                  onSubmit={handleCreateProject}
                  submitText="Crear Proyecto"
                  loading={loading}
                />
              </CardContent>
            </Card>

            {/* Projects List */}
            <Card>
              <CardHeader>
                <CardTitle>Proyectos Existentes ({projects.length})</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {projects.map((project) => (
                    <div key={project.id} className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
                      <div className="flex-1">
                        <h3 className="font-semibold">
                          <div dangerouslySetInnerHTML={{ __html: project.title }} />
                        </h3>
                        <div className="text-sm text-slate-600 line-clamp-2" dangerouslySetInnerHTML={{ __html: project.description }} />
                        {project.actions && project.actions.length > 0 && (
                          <p className="text-xs text-emerald-600 mt-1">
                            {project.actions.length} acción{project.actions.length > 1 ? 'es' : ''}
                          </p>
                        )}
                      </div>
                      <div className="flex gap-2">
                        <Button
                          onClick={() => handleGeneratePrompts(project.id)}
                          variant="outline"
                          size="sm"
                          disabled={loadingProject}
                          className="bg-gradient-to-r from-purple-50 to-pink-50 hover:from-purple-100 hover:to-pink-100 border-purple-200"
                        >
                          <Sparkles className="h-4 w-4 mr-1 text-purple-600" />
                          Prompts
                        </Button>
                        <Button
                          onClick={() => navigate(`/zona-pro/proyecto/${project.id}`)}
                          variant="outline"
                          size="sm"
                        >
                          Ver/Editar
                        </Button>
                        <Button
                          onClick={() => handleDeleteProject(project.id)}
                          variant="destructive"
                          size="sm"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                  {projects.length === 0 && (
                    <p className="text-center text-slate-500 py-4">No hay proyectos aún</p>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Collaborators Tab */}
        {activeTab === 'collaborators' && (
          <div className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>Añadir Colaborador</CardTitle>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleCreateCollaborator} className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium mb-2">Email *</label>
                    <Input
                      type="email"
                      value={collaboratorForm.email}
                      onChange={(e) => setCollaboratorForm({...collaboratorForm, email: e.target.value})}
                      placeholder="email@ejemplo.com"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2">Contraseña *</label>
                    <Input
                      type="password"
                      value={collaboratorForm.password}
                      onChange={(e) => setCollaboratorForm({...collaboratorForm, password: e.target.value})}
                      placeholder="••••••••"
                      required
                    />
                  </div>
                  <Button type="submit" disabled={loading}>
                    <Plus className="mr-2 h-4 w-4" />
                    Añadir Colaborador
                  </Button>
                </form>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Colaboradores ({collaborators.length})</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {collaborators.map((collab) => (
                    <div key={collab.id} className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
                      <div>
                        <p className="font-medium">{collab.email}</p>
                        <p className="text-sm text-slate-500">Rol: {collab.role}</p>
                      </div>
                      <Button
                        onClick={() => handleDeleteCollaborator(collab.id)}
                        variant="destructive"
                        size="sm"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  ))}
                  {collaborators.length === 0 && (
                    <p className="text-center text-slate-500 py-4">No hay colaboradores aún</p>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Questionnaires Tab */}
        {activeTab === 'questionnaires' && (
          <Card>
            <CardHeader>
              <CardTitle>Respuestas de Cuestionarios ({questionnaires.length})</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {questionnaires.map((q) => (
                  <div key={q.id} className="p-4 bg-slate-50 rounded-lg space-y-2 relative">
                    {/* Delete button */}
                    <Button
                      onClick={() => handleDeleteQuestionnaire(q.id)}
                      variant="ghost"
                      size="sm"
                      className="absolute top-2 right-2 text-red-500 hover:text-red-700 hover:bg-red-50"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                    
                    <div className="flex justify-between items-start pr-10">
                      <div>
                        <h3 className="font-semibold">{q.name}</h3>
                        <p className="text-sm text-slate-600">{q.email}</p>
                        {q.mobile && <p className="text-sm text-slate-600">📱 {q.mobile}</p>}
                        {q.company && <p className="text-sm text-slate-500">🏢 {q.company}</p>}
                      </div>
                      <p className="text-xs text-slate-400">
                        {new Date(q.submitted_at).toLocaleDateString('es-ES', {
                          year: 'numeric',
                          month: 'long',
                          day: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit'
                        })}
                      </p>
                    </div>
                    <div className="mt-3 space-y-3 text-sm">
                      {q.question1 && (
                        <div className="border-l-2 border-emerald-500 pl-3">
                          <p className="font-medium text-slate-700">1. ¿Hay algún producto, servicio, app o proyecto que te haya aportado algo nuevo para tu desempeño profesional?</p>
                          <p className="text-slate-600 mt-1">{q.question1}</p>
                        </div>
                      )}
                      {q.question2 && (
                        <div className="border-l-2 border-emerald-500 pl-3">
                          <p className="font-medium text-slate-700">2. ¿Qué mejoras o nuevas funcionalidades sugerirías?</p>
                          <p className="text-slate-600 mt-1">{q.question2}</p>
                        </div>
                      )}
                      {q.question3 && (
                        <div className="border-l-2 border-emerald-500 pl-3">
                          <p className="font-medium text-slate-700">3. ¿Estarías interesado aprovechar algún producto o servicio del portfolio?</p>
                          <p className="text-slate-600 mt-1">{q.question3}</p>
                        </div>
                      )}
                      {q.question4 && (
                        <div className="border-l-2 border-emerald-500 pl-3">
                          <p className="font-medium text-slate-700">4. ¿Algo que me quieras comentar?</p>
                          <p className="text-slate-600 mt-1">{q.question4}</p>
                        </div>
                      )}
                      {/* Fallback for old format with questions object */}
                      {q.questions && typeof q.questions === 'object' && (
                        <div className="space-y-2">
                          {Object.entries(q.questions).map(([key, value]) => (
                            <div key={key} className="border-l-2 border-slate-400 pl-3">
                              <p className="font-medium text-slate-700">{key}:</p>
                              <p className="text-slate-600 mt-1">{value}</p>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                ))}
                {questionnaires.length === 0 && (
                  <p className="text-center text-slate-500 py-4">No hay cuestionarios enviados aún</p>
                )}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Home Sections Tab */}
        {activeTab === 'home-sections' && (
          <Card>
            <CardHeader>
              <div className="flex justify-between items-center">
                <div>
                  <CardTitle>Secciones de la Home</CardTitle>
                  <CardDescription>
                    Gestiona las secciones entre "Caps" y "Correspondencias"
                  </CardDescription>
                </div>
                <Button
                  onClick={() => {
                    setEditingSection(null);
                    setShowSectionForm(true);
                  }}
                  size="sm"
                >
                  <Plus className="mr-2 h-4 w-4" />
                  Nueva Sección
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {showSectionForm ? (
                <div className="mb-6 p-4 border rounded-lg bg-slate-50">
                  <h3 className="font-medium mb-4">
                    {editingSection ? 'Editar Sección' : 'Crear Nueva Sección'}
                  </h3>
                  <HomeSectionForm
                    initialData={editingSection}
                    onSubmit={editingSection ? handleUpdateSection : handleCreateSection}
                    onCancel={() => {
                      setShowSectionForm(false);
                      setEditingSection(null);
                    }}
                  />
                </div>
              ) : null}

              <div className="space-y-3">
                {homeSections.map((section) => (
                  <div
                    key={section.id}
                    className="flex items-center justify-between p-4 bg-slate-50 rounded-lg border"
                  >
                    <div className="flex-1">
                      <h3 className="font-semibold text-slate-800">
                        {section.title_es || section.title}
                        {section.title_ca && <span className="text-xs text-slate-400 ml-2">+CA</span>}
                        {section.title_en && <span className="text-xs text-slate-400 ml-1">+EN</span>}
                      </h3>
                      <p className="text-sm text-slate-600 line-clamp-2">{section.description_es || section.description}</p>
                      <div className="flex gap-4 mt-2">
                        {section.left_image?.url && (
                          <span className="text-xs text-slate-500">📷 Imagen izq</span>
                        )}
                        {section.right_image?.url && (
                          <span className="text-xs text-slate-500">📷 Imagen der</span>
                        )}
                        <span className={`text-xs ${section.active ? 'text-green-600' : 'text-red-600'}`}>
                          {section.active ? '✓ Activa' : '✗ Inactiva'}
                        </span>
                        <span className="text-xs text-slate-400">Orden: {section.order}</span>
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <Button
                        onClick={() => {
                          setEditingSection(section);
                          setShowSectionForm(true);
                        }}
                        variant="outline"
                        size="sm"
                      >
                        Editar
                      </Button>
                      <Button
                        onClick={() => handleDeleteSection(section.id)}
                        variant="destructive"
                        size="sm"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                ))}
                {homeSections.length === 0 && (
                  <p className="text-center text-slate-500 py-8">
                    No hay secciones creadas. ¡Crea la primera!
                  </p>
                )}
              </div>
            </CardContent>
          </Card>
        )}

        {/* SIG Funnel Content */}
        {activeTab === 'sig-content' && (
          <SigContentForm />
        )}

        {/* ON-IA-GOLF Content */}
        {activeTab === 'on-ia-golf' && (
          <OnIaGolfContentForm />
        )}

        {/* Community Help */}
        {activeTab === 'community-help' && (
          <CommunityHelpForm />
        )}

        {/* Nuts Members */}
        {activeTab === 'nuts-members' && (
          <NutsMembersAdmin />
        )}

        {/* Forum Rooms */}
        {activeTab === 'forum-rooms' && (
          <ForumRoomsAdmin />
        )}

        {/* Pending personal avatars */}
        {activeTab === 'pending-avatars' && (
          <PendingAvatarsAdmin />
        )}

        {/* Polaroid catalogs admin */}
        {activeTab === 'polaroids' && (
          <PolaroidsAdmin />
        )}

        {/* Golf Tours admin */}
        {activeTab === 'golf-tours' && (
          <GolfToursAdmin />
        )}

        {/* Amazon Storefront admin */}
        {activeTab === 'amazon-store' && (
          <AmazonStoreAdmin />
        )}

        {/* Home Video admin */}
        {activeTab === 'home-video' && (
          <HomeVideoAdmin />
        )}

        {/* Home Music admin */}
        {activeTab === 'home-music' && (
          <HomeMusicAdmin />
        )}

        {/* Gallery admin */}
        {activeTab === 'gallery' && (
          <GalleryAdmin />
        )}
          </main>
        </div>
      </div>

      {/* Prompt Generator Dialog */}
      <Dialog open={promptDialogOpen} onOpenChange={setPromptDialogOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Generador de Prompts</DialogTitle>
          </DialogHeader>
          {selectedProject && <PromptGenerator project={selectedProject} />}
        </DialogContent>
      </Dialog>
    </div>
  );
};